import React, { useEffect, useState } from 'react';
import Avatar from '../components/Avatar';
import { api, getMe, getToken } from '../lib/api';
import Loader from '../components/Loader';

export default function PublicProfile({ handle, onBack, onMessage, notify = window.alert }) {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  // Your conversation with them, if you're connected.
  const [link, setLink] = useState(undefined);
  const [asked, setAsked] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.profile(handle).then(setProfile).catch((e) => setError(e.message));
    if (getToken()) api.sharedFiles(handle).then(setFiles).catch(() => setFiles([]));
    if (getToken()) {
      api.connections().then((rows) => setLink(rows.find((c) => c.active && c.other.handle === handle) || null))
        .catch(() => setLink(null));
      api.sentRequests().then((rows) => setAsked(rows.some((r) => r.person.handle === handle))).catch(() => {});
    }
  }, [handle]);

  if (error) return <div className="pe-error">{error}</div>;
  if (!profile) return <Loader />;
  const first = profile.name.split(' ')[0];

  async function sendRequest() {
    setSending(true);
    try {
      const r = await api.askToConnect(profile.id, note.trim());
      if (r.status === 'connected') {
        notify(`Connected with ${profile.name}`);
        api.connections().then((rows) => setLink(rows.find((c) => c.active && c.other.handle === handle) || null)).catch(() => {});
      } else {
        notify(`Request sent to ${first}`);
        setAsked(true);
      }
      setConnecting(false);
    } catch (e) { notify(e.message); } finally { setSending(false); }
  }
  async function block() {
    if (!window.confirm(`Block ${profile.name}? You won't see each other's posts, comments or messages, and any connection ends. They aren't told.`)) return;
    try { await api.block(profile.id); notify(`Blocked ${first}`); onBack(); } catch (e) { notify(e.message); }
  }

  return (
    <div className="pp">
      <button className="linklike" onClick={onBack}>← back</button>
      <div className="pp-card">
        <div className="pp-banner" />
        <Avatar person={profile} className="pp-av" />
        <h1>{profile.name}</h1>
        <p className="pp-headline">{profile.headline}</p>
        {link && link.thread_id && onMessage && (
          <button className="btn primary sm pp-message" onClick={() => onMessage(link.thread_id)}>Message {profile.name.split(' ')[0]}</button>
        )}
        {link === null && profile.id !== getMe()?.id && (
          <div className="pp-notconnected">
            {asked ? <span className="status-chip s-pending_out">Request sent</span>
              : <button className="btn primary sm" onClick={() => setConnecting(true)}>Connect</button>}
            <span className="muted-sm">You're connected when you both say yes.</span>
          </div>
        )}
        {getToken() && profile.id !== getMe()?.id && (
          <button className="linklike muted-sm pp-block" onClick={block}>Block {first}</button>
        )}
        <p className="pp-meta">
          {[profile.location, profile.timezone_label, profile.languages].filter(Boolean).join(' · ')}
          {profile.org_name ? ` · ${profile.org_name}` : ''}
        </p>
        {((profile.role_tags || []).length > 0 || (profile.categories || []).length > 0) && (
          <div className="pp-tags">
            {(profile.role_tags || []).map((r) => <span className="tag role" key={`r-${r}`}>{r}</span>)}
            {(profile.categories || []).map((c) => <span className="tag" key={c}>{c}</span>)}
            {profile.stage && (profile.role_tags || []).includes('founder') && (
              <span className="tag">stage: {profile.stage}</span>
            )}
            {(profile.stages_served || []).length > 0 && (
              <span className="tag">works with: {profile.stages_served.join(', ')}</span>
            )}
          </div>
        )}

        {profile.building && (
          <section><h3>Building</h3><p>{profile.building}</p></section>
        )}
        {profile.working_through && (
          <section><h3>Working through</h3><p>{profile.working_through}</p></section>
        )}

        {profile.intent && (
          <section className="pp-intent">
            <h3>Intent</h3>
            {profile.intent.says_yes_to && (
              <p><b>Would say yes to:</b> {profile.intent.says_yes_to}</p>
            )}
            {profile.intent.seeking && <p><b>Seeking:</b> {profile.intent.seeking}</p>}
            {profile.intent.offering && <p><b>Offering:</b> {profile.intent.offering}</p>}
            {profile.intent.not_looking_for && (
              <p className="pp-negative"><b>Not looking for:</b> {profile.intent.not_looking_for}</p>
            )}
          </section>
        )}

        {files.length > 0 && (
          <section>
            <h3>Files</h3>
            <ul className="pp-evidence">
              {files.map((f) => (
                <li key={f.id}>
                  <span className="chip offer">{f.purpose}</span>{' '}
                  <button className="linklike" onClick={() => api.downloadFile(f.id, f.filename).catch(() => window.alert('That file could not be downloaded. It may have been removed.'))}>
                    {f.filename}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(profile.evidence || []).length > 0 && (
          <section>
            <h3>Evidence</h3>
            <ul className="pp-evidence">
              {profile.evidence.map((e) => (
                <li key={e.id}>
                  <span className="chip offer">{e.kind}</span> {e.label}
                  {e.url && <> — <a href={e.url} target="_blank" rel="noreferrer">link</a></>}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {connecting && (
        <div className="modal-back" role="presentation" onClick={() => setConnecting(false)}>
          <div className="modal card pad" role="dialog" aria-modal="true" aria-labelledby="ppcx-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="ppcx-title">Connect with {profile.name}</h2>
            <p className="muted-sm">Say why. {first} sees your note and your profile, and you're connected only if they accept.</p>
            <label className="flabel" htmlFor="ppcx-note">Your note</label>
            <textarea id="ppcx-note" className="ta" rows={4} maxLength={500} value={note} autoFocus onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Saw your post on PH rules — I'd love to compare notes on licensing." />
            <p className="muted-sm">You can send 3 connection requests a week.</p>
            <div className="modal-actions">
              <button className="btn quiet sm" onClick={() => setConnecting(false)}>Cancel</button>
              <button className="btn apply sm" disabled={note.trim().length < 10 || sending} onClick={sendRequest}>{sending ? 'Sending…' : 'Send request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
